var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Panel from "./Panel.js";
import DomHelper from "../helper/DomHelper.js";
import Toast from "./Toast.js";
import Config from "../Config.js";
import GlobalEvents from "../GlobalEvents.js";
let monacoLoadPromise;
class CodeEditor extends Panel {
  //endregion
  /**
   * Constructor for Code Editor.
   * @param config
   */
  construct(config = {}) {
    const me = this;
    me.editorReady = new Promise((resolve, reject) => {
      me.resolveEditorReady = resolve;
      me.rejectEditorReady = reject;
    });
    super.construct(...arguments);
    me.editorReady.then(() => {
      GlobalEvents.ion({
        theme: ({ theme }) => me.editor.updateOptions({ theme: theme.toLowerCase().includes("dark") ? "vs-dark" : "vs-light" }),
        thisObj: me
      });
      me.element.style.setProperty("--vscode-menu-background", getComputedStyle(me.element.querySelector(".monaco-editor")).getPropertyValue("--vscode-menu-background"));
    });
  }
  get monacoInstance() {
    return globalThis.monaco;
  }
  /**
   * Focuses the Monaco editor
   */
  focus() {
    this.editor.focus();
  }
  // If external code has "primed" the loading already, it can tell us by injecting a Promise
  // which yields the Monaco instance
  static set monacoLoadPromise(p) {
    monacoLoadPromise = p;
  }
  // Allow external code to see if loading has already begun
  static get monacoLoadPromise() {
    return monacoLoadPromise;
  }
  async updateCodePath(codePath, oldCodePath) {
    var _a;
    const me = this;
    if (oldCodePath) {
      (_a = me.loaderScript) == null ? void 0 : _a.remove();
    }
    if (!monacoLoadPromise) {
      monacoLoadPromise = new Promise((resolve) => {
        const onLoad = () => {
          const r = globalThis["require"];
          r.config({ paths: { vs: `${codePath}/min/vs` } });
          r(["vs/editor/editor.main"], function() {
            if (!me.isDestroyed) {
              resolve(globalThis.monaco);
            }
          });
          loader.removeEventListener("error", onError);
          loader.removeEventListener("load", onLoad);
        }, onError = () => {
          const msg = `Code editor path to Monaco editor ${codePath} is incorrect`;
          loader.removeEventListener("error", onError);
          loader.removeEventListener("load", onLoad);
          Toast.show(msg);
          me.rejectEditorReady(msg);
        }, loader = me.loaderScript = DomHelper.createElement({
          tag: "script",
          parent: document.head,
          src: `${codePath}/min/vs/loader.js`
        });
        loader.addEventListener("error", onError);
        loader.addEventListener("load", onLoad);
      });
    }
    globalThis.monaco = await monacoLoadPromise;
    me.getConfig("editor");
    me.getConfig("text");
  }
  updateReadOnly(readOnly) {
    const { readOnly: readOnlyIcon } = this.widgetMap;
    readOnlyIcon && (readOnlyIcon.hidden = !readOnly);
    this.editor.updateOptions({ readOnly });
  }
  updateStatus(status) {
    const { status: statusWidget } = this.widgetMap;
    statusWidget && (statusWidget.html = status);
  }
  changeEditor(editor, oldEditor) {
    var _a, _b;
    const me = this;
    (_a = oldEditor == null ? void 0 : oldEditor.dispose) == null ? void 0 : _a.call(oldEditor);
    if (editor) {
      editor = me.monacoInstance.editor.create(me.contentElement, Config.merge({
        theme: me.theme || ((_b = DomHelper.themeInfo) == null ? void 0 : _b.name.toLowerCase().includes("dark")) ? "vs-dark" : "vs-light",
        padding: {
          top: 14
        }
      }, editor));
      me.monacoInstance.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
        noSuggestionDiagnostics: true
      });
      editor.onDidChangeCursorPosition(me.onCursorMove.bind(me));
      editor.onDidChangeConfiguration(me.onEditorConfigChange.bind(me));
      editor.onDidDispose(me.onEditorDestroy.bind(me));
      me.resolveEditorReady(editor);
    }
    return editor;
  }
  get text() {
    var _a;
    return ((_a = this.codeModel) == null ? void 0 : _a.getValue()) || this._text;
  }
  changeText(text) {
    var _a;
    this._text = (_a = this.codeModel) == null ? void 0 : _a.getValue();
    return text;
  }
  updateText(text) {
    var _a, _b;
    const { codeModel } = this;
    if (text != null) {
      codeModel ? codeModel.setValue(text) : this.loadText(text);
    } else {
      (_b = (_a = this.codeModel) == null ? void 0 : _a.dispose) == null ? void 0 : _b.call(_a);
    }
  }
  /**
   * Loads text into the editor.
   * @param {String} text The text to edit.
   * @param {String} language The language to edit the text with.
   * @returns {Object} The https://microsoft.github.io/monaco-editor/docs.html#interfaces/editor.ITextModel.html
   * which is handling editing the text.
   */
  async loadText(text = this.text, language = this.language) {
    var _a;
    const me = this;
    me._text = text;
    me._language = language;
    (_a = me.codeModel) == null ? void 0 : _a.dispose();
    await me.editorReady;
    const model = me.codeModel = me.monacoInstance.editor.createModel(text, language);
    me.editor.setModel(model);
    me.trigger("load", { model, text });
    return model;
  }
  onEditorDestroy() {
    var _a, _b;
    (_b = (_a = this.codeModel) == null ? void 0 : _a.dispose) == null ? void 0 : _b.call(_a);
    this.codeModel = null;
  }
  onEditorConfigChange() {
    const me = this;
    if (me.editor) {
      me.readOnly = me.editor.getOptions().get(me.monacoInstance.editor.EditorOptions.readOnly.id);
    }
  }
  onCursorMove({ position }) {
    var _a;
    (_a = this.widgetMap.cursorPos) == null ? void 0 : _a.setPosition(position);
  }
}
//region Config
__publicField(CodeEditor, "$name", "CodeEditor");
__publicField(CodeEditor, "type", "codeeditor");
__publicField(CodeEditor, "configurable", {
  textContent: false,
  scrollable: null,
  /**
   * The path from which to load the Monaco editor.
   *
   * For example:
   *
   * ```javascript
   * {
   *   codePath : 'node_modules/monaco-editor'
   * }
   * ```
   *
   * @config {String}
   */
  codePath: null,
  /**
   * The [Monaco](https://microsoft.github.io/monaco-editor/) editor
   * instance. Use this property to manipulate the editor according to
   * https://microsoft.github.io/monaco-editor/typedoc/index.html
   *
   * Note that this will only exist when the editor has been loaded from the {@link #config-codePath}.
   *
   * This will be indicated by the {@link #property-editorReady} property which yields the
   * monaco editor as its value.
   * @member {Object} editor
   * @readonly
   */
  /**
   * An editor configuration object to be passed to https://microsoft.github.io/monaco-editor/docs.html#functions/editor.create.html
   * @config {Object}
   */
  editor: {
    $config: ["lazy", "nullify"],
    value: {
      automaticLayout: true,
      scrollBeyondLastLine: false
    }
  },
  header: {
    cls: "demo-header"
  },
  bbar: {
    overflow: null,
    items: {
      readOnly: {
        hidden: true,
        type: "widget",
        cls: "b-icon b-icon-locked"
      },
      status: {
        type: "widget",
        html: "\xA0"
      },
      cursorPos: {
        type: "widget",
        html: "\xA0",
        setPosition({ lineNumber, column }) {
          this.element.innerHTML = `Ln ${lineNumber}, Col ${column}`;
        }
      }
    }
  },
  /**
   * The read-only state of the editor may be set and read. This state is by default reflected
   * by an icon in the bottom toolbar.
   * @prp {Boolean}
   */
  readOnly: null,
  /**
   * The text being edited may be set and read using this property.
   * @prp {String}
   */
  text: {
    $config: ["lazy", "nullify"],
    value: null
  },
  /**
   * The language being edited may be set and read using this property.
   * @prp {String}
   */
  language: null,
  /**
   * The Monaco theme to use. If not specified, it defaults to `'vs'`;
   * @prp {'vs'|'vs-dark'|'hc-black'|'hc-light'}
   */
  theme: null,
  /**
   * The status message displayed un the bottom toolbar may be set and read using this property.
   * @prp {String}
   */
  status: null
});
CodeEditor.initClass();
CodeEditor._$name = "CodeEditor";
export {
  CodeEditor as default
};
